// src/utils/overallTrajectory.js
//
// The "Overall health trajectory" card: the health score (100 − risk score) at
// each draw, drawn as one line that ends in a dotted "next score" circle. With
// no earlier draw that has a score it is the baseline view (one dot, dashed
// line to the planned re-panel), as in the Figma report. Used by the on-screen
// Trajectory tab and the patient PDF so the two cannot drift; both pass their
// own colour palette.

const healthScoreOf = (t) => {
  const r = Number(t?.consensusRiskScore);
  return Number.isFinite(r) && r > 0 && r <= 100 ? Math.round(100 - r) : null;
};

const esc = (v) => String(v ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const fmt = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Scores oldest → newest, the current draw last. Null when the current draw has no score. */
export function buildOverallTrajectory(triage, history = [], currentDate) {
  const current = healthScoreOf(triage);
  if (current == null) return null;
  const prior = (history || [])
    .map(h => ({ score: healthScoreOf(h), date: new Date(h.drawDate || h.createdAt) }))
    .filter(p => p.score != null && !Number.isNaN(p.date.getTime()))
    .sort((a, b) => a.date - b.date);
  const now = { score: current, date: new Date(currentDate || triage?.drawDate || triage?.createdAt || Date.now()) };
  const points = [...prior, now];
  const previous = prior.length ? prior[prior.length - 1].score : null;
  return { points, current, previous, delta: previous == null ? null : current - previous };
}

/** Self-contained HTML (inline styles only). pal: { ink, muted, line, dark, good, goodBg, amber, amberSoft, card, font } */
export function overallTrajectoryHtml(data, pal) {
  if (!data) return '';
  const { points, current, delta } = data;
  const W = 600, H = 64, X0 = 14, X1 = 442, XN = 586, YT = 18, YB = 52;
  const scores = points.map(p => p.score);
  const lo = Math.min(...scores, 0), hi = Math.max(...scores, 100);
  const y = (s) => YB - ((s - lo) / ((hi - lo) || 1)) * (YB - YT);
  const x = (i) => (points.length === 1 ? X0 : X0 + (i * (X1 - X0)) / (points.length - 1));
  const last = points.length - 1;
  const path = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(p.score).toFixed(1)}`).join(' ');
  const area = `${path} L${x(last).toFixed(1)} ${YB + 8} L${x(0).toFixed(1)} ${YB + 8} Z`;
  const gid = `ot${Math.round(current)}${points.length}`;
  const dots = points.map((p, i) => i === last
    ? `<circle cx="${x(i).toFixed(1)}" cy="${y(p.score).toFixed(1)}" r="8" fill="${pal.goodBg}"/><circle cx="${x(i).toFixed(1)}" cy="${y(p.score).toFixed(1)}" r="5" fill="${pal.dark}"/>`
    : `<circle cx="${x(i).toFixed(1)}" cy="${y(p.score).toFixed(1)}" r="3" fill="${pal.dark}"/>`).join('');
  const labelAt = (i, anchor) => `<text x="${x(i).toFixed(1)}" y="${(y(points[i].score) - 11).toFixed(1)}" text-anchor="${anchor}" font-size="13" font-weight="700" fill="${pal.ink}" font-family="${pal.font}">${points[i].score}</text>`;
  const svg = `<svg viewBox="0 0 ${W} ${H}" width="100%" style="display:block" role="img" aria-label="Overall health trajectory">
  <defs><linearGradient id="${gid}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${pal.muted}" stop-opacity=".16"/><stop offset="1" stop-color="${pal.muted}" stop-opacity="0"/></linearGradient></defs>
  <path d="${area}" fill="url(#${gid})"/>
  ${points.length > 1 ? `<path d="${path}" fill="none" stroke="${pal.dark}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>` : ''}
  <line x1="${x(last).toFixed(1)}" y1="${y(current).toFixed(1)}" x2="${XN}" y2="${y(current).toFixed(1)}" stroke="${pal.amber}" stroke-width="2" stroke-dasharray="3 3"/>
  <circle cx="${XN}" cy="${y(current).toFixed(1)}" r="5" fill="none" stroke="${pal.amber}" stroke-width="1.6" stroke-dasharray="2 2"/>
  ${dots}${labelAt(last, points.length === 1 ? 'start' : 'middle')}${points.length > 1 ? labelAt(0, 'start') : ''}
  <text x="${XN}" y="${(y(current) - 11).toFixed(1)}" text-anchor="end" font-size="11" font-weight="700" fill="${pal.amber}" font-family="${pal.font}">Next score</text>
</svg>`;

  const pillTxt = delta == null ? 'Current baseline' : delta === 0 ? 'No change' : `${delta > 0 ? '↑' : '↓'} ${Math.abs(delta)} since previous`;
  const pillGood = delta == null || delta >= 0;
  const note = delta == null
    ? 'This is your current baseline. Overall movement will appear after the next complete panel provides a comparable health score.'
    : `Your health score is ${delta === 0 ? 'unchanged' : `${delta > 0 ? 'up' : 'down'} ${Math.abs(delta)} point${Math.abs(delta) === 1 ? '' : 's'}`} since your previous draw (${data.previous} → ${current}).`;
  const first = points[0];
  const sm = `font-size:.78em;color:${pal.muted}`;
  return `<div style="font-family:${pal.font};color:${pal.ink};background:${pal.card};border:1px solid ${pal.line};border-radius:18px;padding:16px 18px 14px">
  <div style="display:flex;justify-content:space-between;align-items:center">
    <div style="font-weight:700;font-size:1.1em">Overall health trajectory</div>
    <span style="font-weight:700;font-size:.8em;padding:4px 10px;border-radius:99px;background:${pillGood ? pal.goodBg : pal.badBg};color:${pillGood ? pal.good : pal.bad}">${esc(pillTxt)}</span>
  </div>
  <div style="margin-top:6px">${svg}</div>
  <div style="display:flex;justify-content:space-between;margin-top:4px">
    <div style="${sm}">${esc(fmt(first.date))}<div style="font-weight:700;color:${pal.ink}">${points.length > 1 ? 'First score' : 'Current result'}</div></div>
    <div style="${sm};text-align:right">90-day re-panel<div style="font-weight:700;color:${pal.amber}">Comparison planned</div></div>
  </div>
  <div style="border-top:1px solid ${pal.line};margin-top:10px;padding-top:10px;display:flex;justify-content:space-between;align-items:flex-end;gap:16px">
    <div><div style="font-weight:700;font-size:.72em;letter-spacing:.14em;text-transform:uppercase;color:${pal.muted}">Health score</div><div style="font-weight:700;font-size:1.9em;line-height:1.1">${current}</div></div>
    <div style="${sm};max-width:62%;text-align:right">${esc(note)}</div>
  </div>
</div>`;
}
